import type { UserCapabilities } from '../types';
import { isCurrentSession, type SessionRead } from './session-read';

/**
 * The one channel that keeps `user.capabilities` (auth store) in step with the
 * plan (subscription store).
 *
 * The cohort pages gate on the AUTH user's capability map, which only `/me`
 * used to write — while every plan-change path (the admin debug toggle, an IAP
 * purchase or restore, the Stripe return, a cancellation) refreshes the
 * SUBSCRIPTION store. So a paying customer was told "upgrade to create a
 * cohort" right after buying (2026-10-03). `GET /subscription` now carries the
 * same map `/me` does, and `fetchSubscription()` publishes it here; no call
 * site can forget, because no call site is involved.
 *
 * Two readers of one field need an ORDER. Both `/me` and `/subscription` take a
 * ticket from `issueRead()` (session-read.ts) when the request is ISSUED; the auth
 * store applies a map only if its ticket is newer than the one already on
 * screen. "Newest issued wins" is the right order because each response
 * describes the server as of (roughly) its issue time — an older request that
 * lands late is describing a plan that has since changed.
 *
 * This lives in its own module, rather than the subscription store importing
 * the auth store, because the auth store already imports the subscription
 * store: the sink is registered by the auth store at load.
 */

type CapabilitiesSink = (capabilities: UserCapabilities, readSeq: number) => void;

let sink: CapabilitiesSink | null = null;

/** Registered once, by the auth store, which owns `user.capabilities`. */
export function setCapabilitiesSink(next: CapabilitiesSink): void {
  sink = next;
}

/** Hand a capability map read under `read` to whoever owns the field. */
export function publishCapabilities(capabilities: UserCapabilities, read: SessionRead): void {
  if (!isCurrentSession(read)) return;
  sink?.(capabilities, read.seq);
}

/**
 * Narrow an untrusted response field to a capability map. A server that
 * predates the field (mid-deploy) sends nothing, and nothing must be written.
 */
export function readCapabilities(value: unknown): UserCapabilities | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as UserCapabilities)
    : null;
}
